// Sprint ONB.B2-FIX.D2-FOLLOWON.A1 (#1110) — Invitation-flow redirect detection.
//
// Frontend mirror. Backend duplicate lives at apps/api/src/utils/invitationFlow.ts —
// both files MUST carry identical logic per
// feedback_worktree_shared_exports_invisible (packages/shared exports from a
// worktree don't propagate via @blueprint/shared until merged). Cross-reference
// the backend file at any edit. P3 may consolidate via packages/shared
// post-merge.

export const INVITATION_FLOW_PREFIXES = [
  '/setup/accept',
  '/accept-invite',
  '/client/accept-invite',
  // #1975 (absorbing #1974's orphan-tenant defect) — /academic/accept was
  // missing here from ACT.A1 onward, so an academic invitee registering
  // through the magic link was NOT detected as invitation-flow: POST
  // /auth/register took the solo-signup branch and minted a tenant +
  // TENANT_ADMIN membership + setup_project for a user whose only purpose is
  // to join someone else's tenant. resolveNonClientDefaultTenantId then
  // resolved that orphan as the default, which is why the reported symptom
  // was "user remains in their personal tenant after accepting".
  '/academic/accept',
] as const;

/**
 * Escape regex metacharacters. The current prefixes contain only `/` and
 * alphabetic chars, but a future addition like `/auth/v2/accept` or any
 * prefix with `.`, `*`, `+`, `(`, etc. would otherwise produce a broken
 * pattern. Documented invariant: this function works for any future prefix.
 */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Compile once at module load — avoids per-call RegExp object construction.
// Keyed by prefix (not just an array) so a single prefix's anchored check
// can be reused directly by matchesInvitationFlowPrefix() below, rather than
// a caller writing its own second, independently-anchored (or worse,
// unanchored) implementation of the same concept.
const INVITATION_FLOW_REGEX_BY_PREFIX = new Map(
  INVITATION_FLOW_PREFIXES.map(prefix => [prefix, new RegExp(`^${escapeRegExp(prefix)}(\\?|\\/|$)`)] as const),
);

/**
 * Returns true when the redirect value targets an invitation-acceptance
 * landing page. Used by RegisterPage to detect invitation-flow users and
 * skip Step 2 (workspace creation fields).
 *
 * Match anchored on each prefix followed by `?`, `/`, or end-of-string —
 * `/setup/accept-fake?token=...` MUST NOT match.
 *
 * Defence-in-depth note: this check runs ON TOP OF `isSafeRedirect`
 * (apps/web/src/lib/redirectGuard.ts), not as a replacement.
 */
export function isInvitationFlowRedirect(redirect: string | null | undefined): boolean {
  if (!redirect) return false;
  return [...INVITATION_FLOW_REGEX_BY_PREFIX.values()].some(rx => rx.test(redirect));
}

/**
 * Anchored match against ONE specific invitation-flow prefix, reusing the
 * exact same compiled regex isInvitationFlowRedirect() uses for that prefix
 * — never a second, independently-written anchoring check. #2452 (R1
 * finding) — RegisterPage originally scoped its team-invite token extraction
 * with a bare `redirect.startsWith('/accept-invite')`, which is NOT anchored
 * on the trailing boundary the way isInvitationFlowRedirect() is: a decoy
 * like `/accept-invite-x?token=<real>` fails isInvitationFlowRedirect() (so
 * the page renders the ordinary solo-signup flow) while still passing the
 * unanchored startsWith() check, fetching and locking a real invited email
 * into a page that isn't even in invitation-flow mode. Returns false for an
 * unrecognized prefix rather than matching everything, so a typo'd prefix
 * argument fails loudly in development instead of silently matching every
 * redirect.
 */
export function matchesInvitationFlowPrefix(
  redirect: string | null | undefined,
  prefix: typeof INVITATION_FLOW_PREFIXES[number],
): boolean {
  if (!redirect) return false;
  const regex = INVITATION_FLOW_REGEX_BY_PREFIX.get(prefix);
  return regex ? regex.test(redirect) : false;
}
